/**
 * Согласие на информационную и рекламную рассылку (38-ФЗ ст. 18).
 *
 * Согласие своё у каждого продукта (решение владельца 06.10.2026:
 * продукты не смешивать). Продукт — домен сайта: aisearch.ru,
 * aisearch.tech, ingdg.com, datahunter.store. API берёт его из хоста
 * запроса, поэтому согласие, отказ и отписка на одном сайте касаются
 * только писем этого сайта. На aisearch.ru это рассылка AiSearch;
 * Конспект — программа для Windows, aisearch.ru для неё только витрина,
 * и в согласие она не входит.
 *
 * Модуль общий для браузера и сервера: здесь только константы, разбор
 * ответа и работа с localStorage. Запросы к API — в `lib/api/soglasie.ts`,
 * он серверный.
 */

/**
 * Редакция текста согласия.
 *
 * Уходит в API вместе с решением: при споре нужно показать, под каким
 * именно текстом человек поставил галочку. Меняется вместе с текстом
 * документа на всех сайтах.
 */
export const SOGLASIE_VERSIYA = 'v2-2026-10';

/** Где человек принял решение. Нужен API для журнала согласий. */
export type IstochnikSoglasiya = 'register' | 'kabinet' | 'banner';

/** Исход отписки по ссылке из письма. */
export type IshodOtpiski = 'ok' | 'plohaya_ssylka' | 'nedostupno';

/**
 * Как часто писать: обычно или «реже, только о важном».
 *
 * «Реже» — мягкий выбор на странице отписки для тех, кому писем просто
 * много: человек остаётся, а не уходит совсем.
 */
export type Chastota = 'obychno' | 'redko';

/** Причина отписки. В API уходит только она и продукт, без адреса. */
export type PrichinaOtpiski = 'chasto' | 'neinteresno' | 'ne_podpisyvalsya' | 'drugoe';

export const PRICHINY_OTPISKI: readonly PrichinaOtpiski[] = [
  'chasto',
  'neinteresno',
  'ne_podpisyvalsya',
  'drugoe',
];

/** Что API знает о ссылке отписки: кому она и что сейчас решено. */
export interface SvedeniyaOtpiski {
  /** Адрес под маской (`va***@gmail.com`): целиком на страницу не выводится. */
  email_masked: string;
  /** Продукт ссылки — домен сайта. */
  domain: string;
  value: boolean | null;
  chastota: Chastota;
  /**
   * Контрольная копия (seed) или проба: кнопки как в настоящем письме,
   * но ничего не записывается. Страница говорит это прямо, а не «Готово».
   */
  control_copy?: boolean;
}

/**
 * Проверка ссылки до нажатия.
 *
 * `nedostupno` — API не ответил или ещё не знает этой ручки: тогда
 * страница всё равно показывает кнопку, без адреса и продукта.
 */
export type ProverkaSsylki = SvedeniyaOtpiski | 'plohaya_ssylka' | 'nedostupno';

/** Исход «прислать ссылку для отписки». */
export type IshodSsylki = 'ok' | 'chasto' | 'nedostupno';

/** Состояние согласия, как его отдаёт API. `value: null` — решения не было. */
export interface SoglasieRassylki {
  value: boolean | null;
  source: string | null;
  text_version: string | null;
  domain: string | null;
  updated_at: string | null;
  chastota: Chastota;
}

function razobratChastotu(v: unknown): Chastota {
  return v === 'redko' ? 'redko' : 'obychno';
}

/**
 * Разбор ответа о ссылке отписки. Чужая форма — `null`: страница тогда
 * ведёт себя как при недоступном API, а не выдумывает адрес.
 */
export function razobratSvedeniya(data: unknown): SvedeniyaOtpiski | null {
  if (typeof data !== 'object' || data === null) return null;
  const d = data as Record<string, unknown>;
  if (typeof d.email_masked !== 'string' || typeof d.domain !== 'string') return null;
  if (d.value !== true && d.value !== false && d.value !== null) return null;
  return {
    email_masked: d.email_masked,
    domain: d.domain,
    value: d.value,
    chastota: razobratChastotu(d.chastota),
    control_copy: d.control_copy === true,
  };
}

/**
 * Решение с регистрации, ждущее первого входа.
 *
 * Регистрацию ведёт Authentik, и сохранить галочку ему негде: аккаунта
 * в нашем API ещё нет, токена тоже. Поэтому решение ждёт в браузере до
 * первого открытия кабинета и уходит оттуда (`OtlozhennoeSoglasie`).
 */
export const KLYUCH_OTLOZHENNOGO = 'ais-soglasie-rassylka';

/**
 * «Не сейчас» у предложения в кабинете.
 *
 * Это не отказ, и в API он не уходит: человек просто отложил вопрос.
 * Записать отказ значило бы решить за него.
 */
export const KLYUCH_SKRYTO = 'ais-soglasie-skryto';

/** Адрес документа на aisearch.ru: русский реестр (`ais-legal.ts`). */
export const SLAG_SOGLASIYA_RU = 'soglasie-na-rassylku';

/** Адрес документа на остальных сайтах: зарубежный реестр. */
export const SLAG_SOGLASIYA_ZARUBEZH = 'marketing-consent';

/**
 * Ссылка на текст согласия того сайта, где стоит галочка.
 *
 * Человек соглашается с документом своего сайта: на aisearch.ru это
 * русский текст под ИП и 152-ФЗ, на остальных — английская редакция
 * (es, sr — свой перевод). Префикс языка как у остальных страниц:
 * английский без префикса (as-needed), на русском домене префикса нет
 * вовсе.
 */
export function adresSoglasiya(ruDomen: boolean, locale: string): string {
  if (ruDomen) return `/legal/${SLAG_SOGLASIYA_RU}`;
  const prefiks = locale === 'en' ? '' : `/${locale}`;
  return `${prefiks}/legal/${SLAG_SOGLASIYA_ZARUBEZH}`;
}

/**
 * Разбор ответа API.
 *
 * Чужая форма ответа превращается в `null`, а не в «решения нет»:
 * показать предложение человеку, который уже отказался, хуже, чем не
 * показать его вовсе.
 */
export function razobratSoglasie(data: unknown): SoglasieRassylki | null {
  if (typeof data !== 'object' || data === null) return null;
  const d = data as Record<string, unknown>;
  if (d.value !== true && d.value !== false && d.value !== null) return null;
  const stroka = (v: unknown) => (typeof v === 'string' ? v : null);
  return {
    value: d.value,
    source: stroka(d.source),
    text_version: stroka(d.text_version),
    domain: stroka(d.domain),
    updated_at: stroka(d.updated_at),
    chastota: razobratChastotu(d.chastota),
  };
}

/** Минимум от localStorage: так логику можно проверить без браузера. */
export interface Hranilishche {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

/**
 * localStorage, если он есть.
 *
 * В приватном режиме Safari и при запрете сайтовых данных обращение
 * бросает исключение. Галочка — не повод ломать регистрацию.
 */
export function brauzernoeHranilishche(): Hranilishche | null {
  try {
    return typeof window === 'undefined' ? null : window.localStorage;
  } catch {
    return null;
  }
}

/**
 * Запомнить решение с формы регистрации.
 *
 * Снятая галочка стирает запись, а не пишет «нет»: отказ на регистрации
 * ничем не отличается от отсутствия решения, и записывать его не нужно.
 * Стирание же важно при повторной отправке формы: человек поставил
 * галочку, получил ошибку пароля, передумал и снял её.
 */
export function zapomnitSRegistracii(
  hranilishche: Hranilishche | null,
  hochet: boolean,
  sejchas: Date = new Date(),
): void {
  if (!hranilishche) return;
  try {
    if (hochet) {
      hranilishche.setItem(
        KLYUCH_OTLOZHENNOGO,
        JSON.stringify({ value: true, at: sejchas.toISOString() }),
      );
    } else {
      hranilishche.removeItem(KLYUCH_OTLOZHENNOGO);
    }
  } catch {
    // Хранилище переполнено или закрыто: человек увидит предложение в
    // кабинете и решит там.
  }
}

/** Есть ли решение с регистрации, ещё не отправленное в API. */
export function estOtlozhennoe(hranilishche: Hranilishche | null): boolean {
  if (!hranilishche) return false;
  try {
    const syroe = hranilishche.getItem(KLYUCH_OTLOZHENNOGO);
    if (syroe === null) return false;
    const data = JSON.parse(syroe) as { value?: unknown };
    return data.value === true;
  } catch {
    return false;
  }
}

/**
 * Отправить решение с регистрации и стереть его.
 *
 * Запись стирается только после ответа API: если он не принял (сеть,
 * бэкенд ещё не выкачен), попытка повторится при следующем открытии
 * кабинета. Битая запись стирается сразу — повторять её незачем.
 *
 * Возвращает, ушёл ли запрос, — для проверки и для предложения в
 * кабинете: пока решение не отправлено, спрашивать человека снова нельзя.
 */
export async function otpravitOtlozhennoe(
  hranilishche: Hranilishche | null,
  sohranit: (value: boolean, source: IstochnikSoglasiya) => Promise<boolean>,
): Promise<boolean> {
  if (!hranilishche) return false;
  let est = false;
  try {
    est = hranilishche.getItem(KLYUCH_OTLOZHENNOGO) !== null;
  } catch {
    return false;
  }
  if (!est) return false;
  if (!estOtlozhennoe(hranilishche)) {
    try {
      hranilishche.removeItem(KLYUCH_OTLOZHENNOGO);
    } catch {
      // Стереть не вышло — нечего и делать.
    }
    return false;
  }
  const ok = await sohranit(true, 'register').catch(() => false);
  if (ok) {
    try {
      hranilishche.removeItem(KLYUCH_OTLOZHENNOGO);
    } catch {
      // Повторная отправка того же решения безвредна.
    }
  }
  return ok;
}

/** Человек отложил вопрос кнопкой «Не сейчас». */
export function skryto(hranilishche: Hranilishche | null): boolean {
  try {
    return hranilishche?.getItem(KLYUCH_SKRYTO) === '1';
  } catch {
    return false;
  }
}

/** Отложить вопрос. Только локально: в API ничего не уходит. */
export function skryt(hranilishche: Hranilishche | null): void {
  try {
    hranilishche?.setItem(KLYUCH_SKRYTO, '1');
  } catch {
    // Не запомнилось — предложение покажется ещё раз, это не беда.
  }
}

/**
 * Показывать ли предложение в кабинете.
 *
 * Только если решения нет вовсе (`value === null`): согласие и отказ,
 * принятые раньше, переспрашивать нельзя. Ответ API, который не удалось
 * разобрать, тоже означает «не показывать».
 */
export function pokazatPredlozhenie(
  soglasie: SoglasieRassylki | null,
  hranilishche: Hranilishche | null,
): boolean {
  if (soglasie === null || soglasie.value !== null) return false;
  if (skryto(hranilishche)) return false;
  if (estOtlozhennoe(hranilishche)) return false;
  return true;
}

/**
 * Строки страницы отписки. Страница серверная и знает язык и бренд
 * домена, поэтому отдаёт их кнопкам готовыми, с подставленным брендом.
 */
export const KLYUCHI_OTPISKI = [
  'pochta',
  'rassylka',
  'poyasnenie',
  'knopka',
  'redko',
  'redko_gotovo',
  'uzhe',
  'gotovo',
  'vernut',
  'vernuli',
  'prichina_vopros',
  'prichina_chasto',
  'prichina_neinteresno',
  'prichina_ne_podpisyvalsya',
  'prichina_drugoe',
  'prichina_spasibo',
  'sluzhebnye',
  'bez_ssylki',
  'plohaya_ssylka',
  'kabinet_zagolovok',
  'kabinet',
  'v_nastrojki',
  'pochta_zagolovok',
  'pochta_poyasnenie',
  'pole_pochta',
  'prislat',
  'prislano',
  'chasto',
  'nedostupno',
  'kontrolnaya',
  'kontrolnaya_gotovo',
] as const;

export type TekstyOtpiski = Record<(typeof KLYUCHI_OTPISKI)[number], string>;

/**
 * Строки страницы согласия по ссылке из служебного письма
 * (`/soglasie?t=…&r=…`). Как у отписки: страница серверная и отдаёт их
 * кнопке готовыми, с подставленным брендом. Текст самого согласия со
 * ссылкой на документ собирается на сервере отдельно (в нём ссылка).
 */
export const KLYUCHI_SOGLASIYA = [
  'pochta',
  'poyasnenie',
  'knopka',
  'gotovo',
  'uzhe',
  'plohaya_ssylka_zagolovok',
  'plohaya_ssylka',
  'v_kabinet',
  'nedostupno',
] as const;

export type TekstySoglasiya = Record<(typeof KLYUCHI_SOGLASIYA)[number], string>;
